import React, { useEffect, useState } from "react";
import { useField } from "formik";
import { ErrorMessage, Flex, HelpIndicator, Icon, Text, useDebouncedFn } from "@jsq/ds/core";
import { useFormContext, FormTextInput } from "@jsq/ds/forms";
import { FieldGroup } from "../../../../../common/components/Form/Layout/FieldGroup";
import { MultiInputFormRow } from "../../../../../common/components/Form/Layout/MultiInputFormRow";
import { useValidateRoutingNumberLazyQuery } from "../../../../../graphql/supergraph.index";
import { PaymentsRoutingNumberType } from "../../../../../graphql/supergraphOperations";
import { type HandleValidateParams } from "../../../hooks/use-bank-validation";
import {
  SWIFT_CODE_NUMBER_MAX_LENGTH,
  SWIFT_CODE_NUMBER_MIN_LENGTH,
  SwiftCodeInputFields,
} from "./constants";
import { swiftCodeInput } from "./styles_vanilla.css";

interface FormFieldKeysType {
  swiftCode: string;
  bankName: string;
}

export interface SwiftCodeInputProps {
  onValidationCompleted: (params: HandleValidateParams) => void;
  onClear: () => void;
  formFieldKeys?: FormFieldKeysType;
}

export const SwiftCodeInput: React.FC<SwiftCodeInputProps> = ({
  onValidationCompleted,
  onClear,
  formFieldKeys = {
    swiftCode: SwiftCodeInputFields.SwiftCode,
    bankName: SwiftCodeInputFields.BankName,
  },
}) => {
  const [routingNumberField, routingNumbersHelpers] = useField<string>(formFieldKeys.swiftCode);
  const { setFieldTouched } = useFormContext();
  const [showBankValidatorError, setShowBankValidatorError] = useState(false);

  /**
   * Clear the local validated bank country if the SWIFT number field is cleared.
   */
  useEffect(() => {
    if (!routingNumberField.value) {
      if (onClear) {
        onClear();
      }
    }
  }, [routingNumberField.value, onClear]);

  const [doValidateRoutingNumber] = useValidateRoutingNumberLazyQuery({
    onCompleted: (data) => {
      const { bankName, bankAddress, isValid } = data?.validateRoutingNumber || {};

      if (onValidationCompleted) {
        onValidationCompleted({
          setShowBankValidatorError,
          bankName,
          bankAddress,
          isValid,
        });
      }
    },
  });

  // TODO: fix this
  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  const debouncedValidateRoutingNumber = useDebouncedFn(doValidateRoutingNumber, 500);

  useEffect(() => {
    if (
      [SWIFT_CODE_NUMBER_MAX_LENGTH, SWIFT_CODE_NUMBER_MIN_LENGTH].includes(
        routingNumberField?.value?.length,
      ) &&
      !routingNumbersHelpers.error
    ) {
      debouncedValidateRoutingNumber({
        variables: {
          validateRoutingNumberInput: {
            // Capitalize the input before validating since letters in Swift codes should always be capitalized
            routingNumber: routingNumberField.value.toUpperCase(),
            routingNumberType: PaymentsRoutingNumberType.Swift,
          },
        },
      });
    } else {
      // Clear bankName and bankAddress fields
      if (onClear) {
        onClear();
      }
      setShowBankValidatorError(false);
    }
  }, [
    routingNumberField.value,
    routingNumbersHelpers.error,
    debouncedValidateRoutingNumber,
    onClear,
  ]);

  return (
    <FieldGroup>
      <MultiInputFormRow>
        <Flex flexDirection="column">
          {/* Note that we autocapitalize any string entered into this input field via CSS */}
          {/* In JS, we transform the value before validating or sending it to the BE for validation or submission */}
          <FormTextInput
            name={formFieldKeys.swiftCode}
            className={swiftCodeInput}
            isFullWidth
            onPaste={() => {
              void setFieldTouched(formFieldKeys.swiftCode, true, true);
            }}
            label={
              <HelpIndicator
                id="swift-code-help-indicator"
                placement="right"
                data-cy="help-icon"
                content="A SWIFT code, also known as a BIC (Bank Identifier Code)
              is a code that identifies banks and financial institutions.
              Your bank's SWIFT code can be found on your bank account
              statement near your account and routing numbers."
              >
                <Text>
                  Swift / BIC code{" "}
                  <Icon type="question-circle" size="small" variant="outline" />{" "}
                </Text>
              </HelpIndicator>
            }
          />
          {showBankValidatorError && <ErrorMessage>Unrecognized Swift code</ErrorMessage>}
        </Flex>
        <FormTextInput name={formFieldKeys.bankName} isFullWidth label="Bank name" />
      </MultiInputFormRow>
    </FieldGroup>
  );
};
